import { Label } from "../../ui/label";
import { Input } from "../../ui/input";
import { Textarea } from "../../ui/textarea";

interface Category {
  id: string;
  name_en: string;
  name_tr: string;
}

interface Tag {
  id: string;
  name_en: string;
  name_tr: string;
}

interface EditorSettingsPanelProps {
  slug: string;
  setSlug: (s: string) => void;
  date: string;
  setDate: (d: string) => void;
  titleEn: string;
  setTitleEn: (t: string) => void;
  titleTr: string;
  setTitleTr: (t: string) => void;
  summaryEn: string;
  setSummaryEn: (s: string) => void;
  summaryTr: string;
  setSummaryTr: (s: string) => void;
  selectedCategories: string[];
  setSelectedCategories: (c: string[]) => void;
  selectedTags: string[];
  setSelectedTags: (t: string[]) => void;
  categories: Category[];
  tags: Tag[];
  activeLang: "en" | "tr";
  isSlugDisabled: boolean;
}

interface TitleRowProps {
  titleEn: string;
  setTitleEn: (t: string) => void;
  titleTr: string;
  setTitleTr: (t: string) => void;
}

function TitleRow({ titleEn, setTitleEn, titleTr, setTitleTr }: TitleRowProps) {
  return (
    <tr className="hover:bg-muted/5 transition-colors">
      <td className="px-4 py-3 font-medium border-r border-border/40 align-middle">Article Title</td>
      <td className="px-3 py-2 border-r border-border/40">
        <Textarea
          id="title-en"
          placeholder="Enter English Title..."
          value={titleEn}
          onChange={(e) => setTitleEn(e.target.value)}
          className="w-full bg-background/50 border-border/30 focus-visible:ring-1 focus-visible:ring-brand-primary h-9 min-h-[36px] resize-y py-1.5 text-xs"
        />
      </td>
      <td className="px-3 py-2">
        <Textarea
          id="title-tr"
          placeholder="Türkçe Başlık..."
          value={titleTr}
          onChange={(e) => setTitleTr(e.target.value)}
          className="w-full bg-background/50 border-border/30 focus-visible:ring-1 focus-visible:ring-brand-primary h-9 min-h-[36px] resize-y py-1.5 text-xs"
        />
      </td>
    </tr>
  );
}

interface SummaryRowProps {
  summaryEn: string;
  setSummaryEn: (s: string) => void;
  summaryTr: string;
  setSummaryTr: (s: string) => void;
}

function SummaryRow({ summaryEn, setSummaryEn, summaryTr, setSummaryTr }: SummaryRowProps) {
  return (
    <tr className="hover:bg-muted/5 transition-colors">
      <td className="px-4 py-3 font-medium border-r border-border/40 align-middle">
        <div>Summary</div>
        <div className="text-[10px] text-muted-foreground mt-0.5 font-semibold">Max 200 chars</div>
      </td>
      <td className="px-3 py-2 border-r border-border/40">
        <div className="relative">
          <Textarea
            id="summary-en"
            placeholder="English summary..."
            maxLength={200}
            value={summaryEn}
            onChange={(e) => setSummaryEn(e.target.value)}
            className="w-full bg-background/50 border-border/30 focus-visible:ring-1 focus-visible:ring-brand-primary h-9 min-h-[36px] resize-y pr-12 py-1.5 text-xs"
          />
          <span className="absolute right-2 top-2.5 text-[9px] font-bold text-muted-foreground">
            {summaryEn.length}/200
          </span>
        </div>
      </td>
      <td className="px-3 py-2">
        <div className="relative">
          <Textarea
            id="summary-tr"
            placeholder="Türkçe özet..."
            maxLength={200}
            value={summaryTr}
            onChange={(e) => setSummaryTr(e.target.value)}
            className="w-full bg-background/50 border-border/30 focus-visible:ring-1 focus-visible:ring-brand-primary h-9 min-h-[36px] resize-y pr-12 py-1.5 text-xs"
          />
          <span className="absolute right-2 top-2.5 text-[9px] font-bold text-muted-foreground">
            {summaryTr.length}/200
          </span>
        </div>
      </td>
    </tr>
  );
}

interface SlugAndDateRowsProps {
  slug: string;
  setSlug: (s: string) => void;
  date: string;
  setDate: (d: string) => void;
  isSlugDisabled: boolean;
}

function SlugAndDateRows({ slug, setSlug, date, setDate, isSlugDisabled }: SlugAndDateRowsProps) {
  return (
    <>
      <tr className="hover:bg-muted/5 transition-colors">
        <td className="px-4 py-3 font-medium border-r border-border/40 align-middle">Slug (Unique URL Segment)</td>
        <td colSpan={2} className="px-3 py-2">
          <Textarea
            id="editor-slug"
            placeholder="e.g. leveraging-ai"
            value={slug}
            disabled={isSlugDisabled}
            onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-_]/g, "-"))}
            className="w-full bg-background/50 border-border/30 focus-visible:ring-1 focus-visible:ring-brand-primary h-9 min-h-[36px] resize-y py-1.5 text-xs"
          />
        </td>
      </tr>
      <tr className="hover:bg-muted/5 transition-colors">
        <td className="px-4 py-3 font-medium border-r border-border/40 align-middle">Publish Date</td>
        <td colSpan={2} className="px-3 py-2">
          <Input
            id="publish-date"
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="w-full bg-background/50 border-border/30 focus-visible:ring-1 focus-visible:ring-brand-primary h-9 text-xs"
          />
        </td>
      </tr>
    </>
  );
}

interface TaxonomyRowProps {
  selectedCategories: string[];
  toggleCat: (id: string, checked: boolean) => void;
  selectedTags: string[];
  toggleTag: (id: string, checked: boolean) => void;
  categories: Category[];
  tags: Tag[];
  activeLang: "en" | "tr";
}

function TaxonomyRow({
  selectedCategories, toggleCat,
  selectedTags, toggleTag,
  categories, tags, activeLang
}: TaxonomyRowProps) {
  return (
    <tr className="hover:bg-muted/5 transition-colors">
      <td className="px-4 py-3 font-medium border-r border-border/40 align-middle">Taxonomy Select</td>
      <td colSpan={2} className="px-3 py-3">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label className="text-[10px] text-muted-foreground uppercase font-bold tracking-wider">Categories</Label>
            <div className="border border-border/40 bg-background/40 rounded-lg p-2.5 h-28 overflow-y-auto space-y-1.5 text-xs text-foreground/80">
              {categories.length === 0 ? (
                <span className="italic text-muted-foreground">No categories defined.</span>
              ) : (
                categories.map((cat) => (
                  <label key={cat.id} className="flex items-center gap-2 cursor-pointer select-none hover:text-foreground">
                    <input
                      type="checkbox"
                      checked={selectedCategories.includes(cat.id)}
                      onChange={(e) => toggleCat(cat.id, e.target.checked)}
                      className="rounded border-border text-brand-primary focus:ring-brand-primary h-3.5 w-3.5"
                    />
                    <span>{activeLang === "tr" ? cat.name_tr : cat.name_en}</span>
                  </label>
                ))
              )}
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-[10px] text-muted-foreground uppercase font-bold tracking-wider">Tags</Label>
            <div className="border border-border/40 bg-background/40 rounded-lg p-2.5 h-28 overflow-y-auto space-y-1.5 text-xs text-foreground/80">
              {tags.length === 0 ? (
                <span className="italic text-muted-foreground">No tags defined.</span>
              ) : (
                tags.map((tag) => (
                  <label key={tag.id} className="flex items-center gap-2 cursor-pointer select-none hover:text-foreground">
                    <input
                      type="checkbox"
                      checked={selectedTags.includes(tag.id)}
                      onChange={(e) => toggleTag(tag.id, e.target.checked)}
                      className="rounded border-border text-brand-primary focus:ring-brand-primary h-3.5 w-3.5"
                    />
                    <span>{activeLang === "tr" ? tag.name_tr : tag.name_en}</span>
                  </label>
                ))
              )}
            </div>
          </div>
        </div>
      </td>
    </tr>
  );
}

export function EditorSettingsPanel({
  slug, setSlug, date, setDate,
  titleEn, setTitleEn, titleTr, setTitleTr,
  summaryEn, setSummaryEn, summaryTr, setSummaryTr,
  selectedCategories, setSelectedCategories,
  selectedTags, setSelectedTags,
  categories, tags, activeLang, isSlugDisabled,
}: EditorSettingsPanelProps) {
  const toggleCat = (id: string, checked: boolean) => {
    if (checked) setSelectedCategories([...selectedCategories, id]);
    else setSelectedCategories(selectedCategories.filter((c: string) => c !== id));
  };
  const toggleTag = (id: string, checked: boolean) => {
    if (checked) setSelectedTags([...selectedTags, id]);
    else setSelectedTags(selectedTags.filter((t: string) => t !== id));
  };

  return (
    <div className="p-5 border border-border/60 bg-card/65 rounded-2xl space-y-4 text-left">
      <h5 className="font-bold text-xs text-brand-primary uppercase tracking-wider">
        Article Details & Metadata
      </h5>

      <div className="overflow-x-auto border border-border/60 rounded-xl bg-card/30">
        <table className="min-w-full divide-y divide-border/40 text-sm">
          <thead className="bg-muted/40 font-semibold text-brand-secondary">
            <tr>
              <th className="px-4 py-2.5 text-left w-1/4 border-r border-border/40 text-xs uppercase tracking-wider">Field</th>
              <th className="px-4 py-2.5 text-left w-3/8 border-r border-border/40 text-xs uppercase tracking-wider">English (EN)</th>
              <th className="px-4 py-2.5 text-left w-3/8 text-xs uppercase tracking-wider">Turkish (TR)</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border/40 text-left">
            <TitleRow titleEn={titleEn} setTitleEn={setTitleEn} titleTr={titleTr} setTitleTr={setTitleTr} />
            <SummaryRow summaryEn={summaryEn} setSummaryEn={setSummaryEn} summaryTr={summaryTr} setSummaryTr={setSummaryTr} />
            <SlugAndDateRows slug={slug} setSlug={setSlug} date={date} setDate={setDate} isSlugDisabled={isSlugDisabled} />
            <TaxonomyRow
              selectedCategories={selectedCategories} toggleCat={toggleCat}
              selectedTags={selectedTags} toggleTag={toggleTag}
              categories={categories} tags={tags} activeLang={activeLang}
            />
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default EditorSettingsPanel;
